import { Grid, LinearProgress } from "@mui/material";
import { useDebouncedEffect } from "@react-hookz/web";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useBrandTones } from "../brand-kit/hooks/use-brand-tones";
import { brandingAtom } from "../brand-kit/store";
import { DiscoverCard } from "./carousel/discover-card";
import { programNameGetter } from "./carousel/discover-carousels";
import { discoverSearchQuery, discoverSearchResultsAtom, unstableAtlasSearchAtom } from "./store";

import type { DiscoverTemplate } from "./types";
import type { GridProps } from "@mui/material";
import type { FC } from "react";

export const DiscoverSearchResults: FC<GridProps> = ({ ...props }) => {
  const [searchParams] = useSearchParams();
  const q = searchParams.get("q");
  const unstable = searchParams.get("unstable") === "1";
  const navigate = useNavigate();
  const setQuery = useSetAtom(discoverSearchQuery);
  const setUnstableDiscoverAtom = useSetAtom(unstableAtlasSearchAtom);
  const resultsLoader = useAtomValue(discoverSearchResultsAtom);

  const branding = useAtomValue(brandingAtom);
  const { data: brandTones, loading } = useBrandTones();
  const brandTone = useMemo(() => brandTones?.find((b) => b.is_default), [brandTones]);

  useEffect(() => {
    setUnstableDiscoverAtom(unstable);
  }, [unstable, setUnstableDiscoverAtom]);

  useDebouncedEffect(
    () => {
      setQuery(q ?? null);
    },
    [q],
    500,
  );

  const handleClick = (e: React.MouseEvent<HTMLDivElement>, template: DiscoverTemplate): void => {
    void navigate({
      pathname: `/discover/${template.program.slug}/${template.slug}`,
      ...(brandTone
        ? {
            search: `?${new URLSearchParams({
              tone: brandTone?.id,
            }).toString()}`,
          }
        : {}),
    });
  };

  return (
    <Grid
      container
      columnSpacing={3}
      rowSpacing={4}
      columns={{ xs: 2, sm: 3, md: 3, lg: 4, xl: 5 }}
      {...props}
    >
      {(resultsLoader.state === "loading" || loading) && (
        <Grid size={{ xs: 12 }}>
          <LinearProgress />
        </Grid>
      )}
      {resultsLoader.state === "hasError" && (
        <Grid size={{ xs: 12 }}>
          Sorry, we seem to be having trouble with that. Please try again later. If the problem
          persists, please contact support.
        </Grid>
      )}
      {resultsLoader.state === "hasData" &&
        !loading &&
        resultsLoader.data?.map((template) => (
          <Grid key={template.slug} size={{ xs: 1 }} minWidth={250}>
            <DiscoverCard
              template={template}
              onClick={handleClick}
              brandKitSelections={branding}
              subheadingGetter={(_template: DiscoverTemplate): string | null =>
                programNameGetter(_template)
              }
            />
          </Grid>
        ))}
    </Grid>
  );
};
